import isEqual from 'lodash.isequal';
import type { FlagsVariant, Mixpanel } from 'mixpanel-browser';

import type { FEATURE } from '~/common/enums/feature.enum';

export type FlagIdentity = {
  userId?: string;
  anonymousId: string;
  isInternal: boolean;
  isTester: boolean;
  isWebView: boolean;
};

// Replaced on every identity change, so an await that resumes holding an older session is stale.
export type IdentitySession = {
  distinctId: string;
  hasNetworkDataForIdentity: boolean;
  lastFetchAt: number;
  exposed: Set<FEATURE>;
  deferredExposures: Set<FEATURE>;
  /** From an identity change until its fetch settles, the SDK may still hold the previous identity's flags. */
  fetchPending: boolean;
};

export type FlagsContext = {
  distinct_id: string;
  device_id: string;
  custom_properties: {
    platform: 'web';
    isInternal: boolean;
    isTester: boolean;
    isWebView: boolean;
  };
};

export const newSession = (distinctId: string, fetchPending: boolean): IdentitySession => ({
  distinctId,
  hasNetworkDataForIdentity: false,
  lastFetchAt: 0,
  exposed: new Set(),
  deferredExposures: new Set(),
  fetchPending,
});

export const toContext = ({
  userId,
  anonymousId,
  isInternal,
  isTester,
  isWebView,
}: FlagIdentity): FlagsContext => ({
  distinct_id: userId ?? anonymousId,
  device_id: anonymousId,
  custom_properties: { platform: 'web', isInternal, isTester, isWebView },
});

const sameVariant = (a: FlagsVariant, b: FlagsVariant | undefined) =>
  !!b &&
  a.key === b.key &&
  isEqual(a.value, b.value) &&
  a.experiment_id === b.experiment_id &&
  a.is_experiment_active === b.is_experiment_active;

export const sameVariants = (
  next: Record<string, FlagsVariant>,
  current: Record<string, FlagsVariant>
) => {
  const keys = Object.keys(next);
  return (
    keys.length === Object.keys(current).length &&
    keys.every((flag) => sameVariant(next[flag], current[flag]))
  );
};

export const markFetched = (session: IdentitySession) => {
  session.hasNetworkDataForIdentity = true;
  session.lastFetchAt = Date.now();
  session.fetchPending = false;
};

// The SDK seeds its own `$device:<uuid>` identity on init and reset(). Replace it with Segment's
// anonymous ID so SDK-tracked events ($experiment_started, $identify) join Segment's events.
// distinct_id is left alone once it is the signed-in user, so reloads don't re-send $identify.
export const adoptAnonymousId = (sdk: Mixpanel, anonymousId: string, userId?: string) => {
  sdk.register({ $device_id: anonymousId });
  if (sdk.get_distinct_id() !== userId) sdk.register({ distinct_id: anonymousId });
};

export const switchToUser = (sdk: Mixpanel, context: FlagsContext, userId: string) => {
  sdk.flags.update_context(context, { replace: true });
  adoptAnonymousId(sdk, context.device_id, userId);
  sdk.identify(userId);
};

// reset() clears the flags and fetches only after an async storage clear, so update_context()
// must come second for when_ready() to wait on a real fetch with the new context.
export const switchToAnonymous = (sdk: Mixpanel, context: FlagsContext) => {
  sdk.reset();
  adoptAnonymousId(sdk, context.device_id);
  sdk.flags.update_context(context, { replace: true });
};
